/**
 * Which VAT rates this register can actually register.
 *
 * **Moved here from `manager-kasa/fiskal-settings` by §9.327**, with the three pickers it guards.
 * The owner asked twice for one place - *"I am pretty sure you can do it only in one place, not on
 * two places these Sadzby DPH"* - and §9.324 had kept the pickers on *Pokladňa → Nastavenia*
 * precisely because moving them would have orphaned this file. **The guard moves with them rather
 * than being deleted**, which is the only version of *one place* worth having.
 *
 * It also belongs here: every rule below is about `vat_categories`, which is this screen's own
 * table. It was in the register's folder because the register's screen happened to be the only
 * caller.
 *
 * A rate does not reach the device as a percentage. It reaches it as a **letter**
 * — A, B, C or D — and the letter is turned into a device slot index the operator
 * has confirmed against the machine (§10A.3). Two things can therefore go wrong
 * with a rate somebody types into the settings screen, and legacy checks neither:
 *
 *  1. **The letter is not the rate.** The banding is fixed:
 *     `0 → D`, `≤5 → A`, `≤19 → B`, `>19 → C` (`VatLetters.forRate` in
 *     `backend/common`, one definition shared by fiskal, orders and invoicing).
 *     So a rate of 10 % prints under **B**, and B on this franchise is the 19 %
 *     rate. The receipt would look correct and register the wrong tax.
 *  2. **The letter may have no confirmed slot.** The backend refuses to print
 *     rather than guess an index (`FiskalReceiptService.slotFor`), because sending
 *     23 % to a slot that is actually 0 % registers the wrong tax on a document
 *     that looks perfectly correct. A rate whose letter is unconfirmed stops the
 *     till.
 *
 * Legacy offers a hardcoded preset list — 0, 5, 10, 19, 20 — written before the
 * 2025 rates. Against this franchise's own categories, **two of those five are
 * wrong**: 10 % bands onto the 19 % letter, and 20 % bands onto C, which is 23 %
 * here *and* has no confirmed slot. The rate the franchise actually charges as
 * standard, 23 %, is not in the list at all.
 *
 * So the options are built from `GET /vat-categories` — the operator-confirmed
 * source of truth — and anything else is checked and explained.
 */

export type VatLetter = 'A' | 'B' | 'C' | 'D'

export interface VatCategory {
  id?: number
  name?: string
  letter?: string
  /** A percentage, as a `numeric` string. Not money — never rounded to cents. */
  rate?: string | number
  /** The device's own index for this letter. `null` until somebody has confirmed it. */
  fiskalSlot?: number | null
  isDefault?: boolean
  isActive?: boolean
  sortOrder?: number
}

/**
 * The letter a rate prints under.
 *
 * Mirrors `VatLetters.forRate` in the backend exactly, band for band. Copying a
 * banding rule is normally how the copies drift — but this one decides what a
 * screen is allowed to *offer*, and a frontend that cannot answer the question
 * cannot warn about it. The test names the backend file so the pair is found
 * together.
 */
export function letterForRate(rate: number): VatLetter {
  if (rate <= 0) return 'D'
  if (rate <= 5) return 'A'
  return rate <= 19 ? 'B' : 'C'
}

/** A percentage is a multiplicand, not an amount somebody pays — §14.7 does not apply. */
export function parseRate(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

export interface RateCheck {
  /** The typed rate, or `null` if it is not a number. */
  rate: number | null
  /** The letter it would print under. */
  letter: VatLetter | null
  /** The franchise's category for that letter, if it has one. */
  category: VatCategory | null
  /** Whether that category's own rate is the rate being set. */
  matchesCategory: boolean
  /** Whether the device index for that letter has been confirmed. */
  slotConfirmed: boolean
  /** Whether a receipt at this rate would print at all. */
  printable: boolean
}

export function checkRate(value: string | number | null | undefined, categories: VatCategory[]): RateCheck {
  const rate = parseRate(value)
  if (rate === null || rate < 0) {
    return {
      rate: null,
      letter: null,
      category: null,
      matchesCategory: false,
      slotConfirmed: false,
      printable: false,
    }
  }

  const letter = letterForRate(rate)
  const category = categories.find((candidate) => candidate.letter === letter) ?? null
  const categoryRate = category ? parseRate(category.rate) : null
  const slotConfirmed = typeof category?.fiskalSlot === 'number'

  return {
    rate,
    letter,
    category,
    matchesCategory: categoryRate !== null && categoryRate === rate,
    slotConfirmed,
    // No category for the letter at all means the transport's own default table
    // decides, which is what every receipt printed so far has used. That prints.
    printable: category === null ? true : slotConfirmed,
  }
}

/**
 * What is wrong with a rate — or `null`.
 *
 * A code rather than a sentence, so the sentence can be a stable translation key
 * in the component. The two failures are separate because they need different
 * actions: a mis-banded rate is a rate to change, an unconfirmed slot is a number
 * to read off the device with `FRINFO`.
 */
export type RateProblem =
  | { kind: 'not-a-rate' }
  | { kind: 'letter-is-another-rate'; letter: VatLetter; configuredRate: number }
  | { kind: 'slot-unconfirmed'; letter: VatLetter }

export function rateProblem(check: RateCheck): RateProblem | null {
  if (check.rate === null) return { kind: 'not-a-rate' }
  if (check.letter === null) return null

  if (check.category && !check.matchesCategory) {
    return {
      kind: 'letter-is-another-rate',
      letter: check.letter,
      configuredRate: parseRate(check.category.rate) ?? 0,
    }
  }

  // Reported second: a rate that bands onto the wrong letter is wrong whether or
  // not that letter can print, and changing the rate is the fix for both.
  if (!check.printable) return { kind: 'slot-unconfirmed', letter: check.letter }

  return null
}

/** `5`, not `5.00`; `1.5` keeps its half. */
export function formatRate(rate: number): string {
  return Number.isInteger(rate) ? String(rate) : String(rate).replace('.', ',')
}

export interface RateOption {
  /** The stored form — what goes into `vatRateDineIn` and friends. */
  value: string
  label: string
  letter: VatLetter
  slotConfirmed: boolean
}

/**
 * The rates on offer, built from the franchise's own categories rather than a
 * hardcoded list.
 *
 * Inactive categories are dropped — an inactive rate is one the franchise has
 * stopped charging. A category with no confirmed slot is **kept and marked**,
 * because it is a legitimate rate whose device index simply has not been read
 * yet, and hiding it would leave no way to see why printing refuses.
 */
export function rateOptions(categories: VatCategory[]): RateOption[] {
  return categories
    .filter((category) => category.isActive !== false)
    .map((category) => {
      const rate = parseRate(category.rate)
      if (rate === null || !isLetter(category.letter)) return null
      return {
        value: formatRate(rate),
        label: `${formatRate(rate)} % - ${category.name ?? category.letter}`,
        letter: category.letter,
        slotConfirmed: typeof category.fiskalSlot === 'number',
      }
    })
    .filter((option): option is RateOption => option !== null)
    .sort((left, right) => Number(left.value) - Number(right.value))
}

function isLetter(value: string | undefined): value is VatLetter {
  return value === 'A' || value === 'B' || value === 'C' || value === 'D'
}

/** Whether a stored rate is one of the offered options, or a hand-typed one. */
export function isCustomRate(stored: string | number | null | undefined, options: RateOption[]): boolean {
  const rate = parseRate(stored)
  if (rate === null) return true
  return !options.some((option) => parseRate(option.value) === rate)
}
